import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Lock, ArrowRight } from "lucide-react";
import axios from "axios";
import { EASE } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SiteGate({ children }) {
  const [state, setState] = useState("checking");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/auth/check`, { validateStatus: () => true })
      .then((r) => setState(r.status === 200 ? "open" : "locked"))
      .catch(() => setState("locked"));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const res = await axios.post(`${API}/auth/login`, { password }, { validateStatus: () => true });
      if (res.status === 200) {
        setPassword("");
        setState("open");
      } else if (res.status === 429) {
        setError(res.data?.detail || "Too many failed attempts. Please try again in 15 minutes.");
      } else if (res.status === 401) {
        setError("Incorrect password. Please try again.");
      } else {
        setError("Unable to reach the server — try again shortly.");
      }
    } catch {
      setError("Unable to reach the server — try again shortly.");
    } finally {
      setBusy(false);
    }
  };

  if (state === "checking") {
    return (
      <div className="grid min-h-screen place-items-center bg-cream" data-testid="gate-loading">
        <img src="/mr-kwila-logo.png" alt="Mr Kwila" className="h-16 w-auto animate-pulse mix-blend-multiply" />
      </div>
    );
  }

  if (state === "open") return children;

  return (
    <main className="grid min-h-screen place-items-center bg-cream px-5" data-testid="site-gate">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: EASE }}
        className="w-full max-w-md text-center"
      >
        <img
          src="/mr-kwila-logo.png"
          alt="Mr Kwila — Timber, Doors & Furniture"
          className="mx-auto w-full max-w-sm mix-blend-multiply"
          data-testid="gate-logo"
        />
        <p className="mt-10 text-[11px] font-bold uppercase tracking-[0.3em] text-kwila">
          Timber · Doors · Furniture
        </p>
        <h1 className="mt-3 font-display text-4xl font-light tracking-tight md:text-5xl" data-testid="gate-title">
          Login for access
        </h1>
        <form onSubmit={submit} className="mx-auto mt-8 max-w-xs" data-testid="gate-form">
          <div className="flex items-center gap-2 rounded-full border border-ink/20 bg-white px-5 py-1.5 transition-colors focus-within:border-kwila">
            <Lock size={15} className="shrink-0 text-sand" />
            <input
              type="password"
              data-testid="gate-password-input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              autoFocus
              autoComplete="current-password"
              required
              className="w-full bg-transparent py-2.5 text-sm outline-none placeholder:text-sand/70"
            />
            <button
              type="submit"
              data-testid="gate-submit-button"
              disabled={busy}
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-ink text-cream transition-colors hover:bg-kwila disabled:opacity-50"
              aria-label="Login"
            >
              <ArrowRight size={15} />
            </button>
          </div>
          {error && (
            <p role="alert" data-testid="gate-error" className="mt-3 text-sm font-semibold text-kwila">
              {error}
            </p>
          )}
        </form>
      </motion.div>
    </main>
  );
}
